// APIS always reject with error string

const ajax = ({ url, data, ...props }, alertOnError = false) =>
  new Promise(async (resolve, reject) => {
    try {
      const result = await $.ajax({
        url: `${AS.domains.api}${url}`,
        data: data ? JSON.stringify(data) : undefined,
        type: 'POST',
        contentType: 'application/json',
        ...props
      });
      resolve(result);
    } catch (error) {
      if (alertOnError) {
        alert(window.resolveLang('エラーが発生しました。'));
      }
      reject(error);
    }
  });

const routeAjax = ({ url, data, ...props }) =>
  $.ajax({
    url: `${AS.domains.route}${url}`,
    data: data ? JSON.stringify(data) : undefined,
    type: 'POST',
    contentType: 'application/json',
    ...props
  });

const throwAlert = message => {
  alert(message);
  throw new Error(message);
};

export const loginFacebook = (
  device,
  { accessToken, gender, email, name, picture: { data: { url } = {} } = {}, id }
) => {
  if (!id) {
    return Promise.reject();
  }

  return ajax({
    url: `/LOGIN/FACEBOOK/${id}/${device}`,
    data: {
      account: id,
      password: accessToken,
      sex: gender === 'male' ? '1' : '2',
      mail: email,
      nickname: name,
      icon_url: url
    }
  });
};

// ACCOUNT ==============================================
export const AccountUpdate = ({ session, set, unset }) =>
  ajax({
    url: '/ACCOUNT/UPDATE',
    data: {
      session,
      set,
      unset
    }
  });

// AUTH ===============================================
export const AuthSetDeviceToken = ({ session, token, setDevice }) =>
  ajax({
    url: '/AUTH/SET_DEVICE_TOKEN',
    data: {
      session,
      token,
      set_device: setDevice
    }
  });

// MAP ==============================================
export const MapCreate = ({
  session,
  title,
  description,
  image_id,
  share_id,
  dialy_order,
  template_id,
  categories
}) => {
  if (!session) {
    throwAlert(window.resolveLang('セッションが設定されてないです'));
  }

  if (!title) {
    throwAlert(window.resolveLang('マップのタイトルを入力してください'));
  }

  return ajax(
    {
      url: '/MAP/CREATE',
      data: {
        session,
        title,
        description,
        image_id,
        share_id,
        dialy_order,
        template_id,
        categories
      }
    },
    true
  );
};

export const MapUpdate = ({
  session,
  _id,
  title,
  description,
  image_id,
  share_id,
  dialy_order,
  categories
}) => {
  if (!session) {
    throwAlert(window.resolveLang('セッションが設定されてないです'));
  } else if (!_id) {
    throwAlert(window.resolveLang('マップのIDを指定してください'));
  }

  return ajax(
    {
      url: '/MAP/UPDATE',
      data: {
        session,
        _id,
        title,
        description,
        image_id,
        share_id,
        dialy_order,
        categories
      }
    },
    true
  );
};

/**
 * @typedef {{
      skip?: number;
      limit?: number;
      categories?: string[];
      not_categories?: string[];
      prefectures?: string[];
      keyword?: string;
      count?: number;
      random_order?: boolean;
      force_show_account_ids?: string[];
      ids?: string[];
      not_special_contents?: string[];
      locale?: string;
     }} sharedPostData
 */

/**
 * @type {
    (data: sharedPostData) => Promise<{maps: object[], count?: number}>
 * }
 */
export const MapSharedNewest = data =>
  ajax({
    url: '/MAP/SHARED/NEWEST',
    data:
      AS.mapSpecialContents && !data.ids
        ? { ...data, special_contents: AS.mapSpecialContents }
        : data
  });

/**
 * @type {
    (data: sharedPostData) => Promise<{maps: object[], count?: number}>
 * }
 */
export const MapSharedOfficial = data =>
  ajax({
    url: '/MAP/SHARED/OFFICIAL',
    data:
      AS.mapSpecialContents && !data.ids
        ? { ...data, special_contents: AS.mapSpecialContents }
        : data
  });

/**
 * @type {
    (data: sharedPostData) => Promise<{maps: object[], count?: number}>
 * }
 */
export const MapSharedPopular = data =>
  ajax({
    url: '/MAP/SHARED/POPULAR',
    data:
      AS.mapSpecialContents && !data.ids
        ? { ...data, special_contents: AS.mapSpecialContents }
        : data
  });

// SNS ===============================================
export const SnsGood = ({ session, goodFrom, isDelete, locale }) =>
  ajax({
    url: '/SNS/GOOD',
    data: {
      session,
      good_from: goodFrom,
      isDelete,
      locale
    }
  });

export const SnsFollow = ({ session, followFrom, isDelete, locale }) =>
  ajax({
    url: '/SNS/FOLLOW',
    data: {
      session,
      follow_from: followFrom,
      isDelete,
      locale
    }
  });

export const SnsCommentCreate = ({ session, commentFrom, text }) =>
  ajax({
    url: '/SNS/COMMENT/CREATE',
    data: {
      session,
      comment_from: commentFrom,
      text
    }
  });

export const SnsCommentEdit = ({ session, commentId, text }) =>
  ajax({
    url: '/SNS/COMMENT/EDIT',
    data: {
      session,
      comment_id: commentId,
      text
    }
  });

export const SnsCommentDelete = ({ session, _id }) =>
  ajax({
    url: '/SNS/COMMENT/DELETE',
    data: {
      session,
      _id
    }
  });

// NOTIFICATION ===============================================
/*
type result = {
  badge: number;
  notificatons: {
    [notification_id: string]: {
      data: btoa(JSON.stringify({
        type: 'good' | 'follow' | 'comment' | 'createSpot' | 'createDialy';
        sender: string;
        map_id?: string;
        spot_id?: string;
        dialy_id?: string;
      }))
      created_at: string; // has format YYYY-MM-DD HH:mm:ss;
      open_at: string; // has format YYYY-MM-DD HH:mm:ss;
      ... // 他のフィールドは使ってない
    }
  }
  ... // 他のフィールドは使ってない
}
 */
export const MPLFNotifications = async ({ offset, limit, session }) => {
  const { result } = await routeAjax({
    url: '/mplfNotifications',
    data: {
      offset,
      limit,
      session
    }
  });

  return result;
};

export const NotificationDetail = ({
  session,
  locale,
  sender_ids,
  dialy_ids,
  spot_ids,
  map_ids
}) =>
  ajax({
    url: '/NOTIFICATION/DETAIL',
    data: {
      session,
      locale,
      sender_ids,
      dialy_ids,
      spot_ids,
      map_ids
    }
  });

export const MPLFBadgeAllClear = async ({ session }) =>
  routeAjax({
    url: '/mplfBadgeAllClear',
    data: {
      session
    }
  });

export const MPLFRead = async ({ session, notification_id }) =>
  routeAjax({
    url: '/mplfRead',
    data: {
      session,
      notification_id
    }
  });

export const MPLFReadAll = async ({ session }) =>
  routeAjax({
    url: '/mplfReadAll',
    data: {
      session
    }
  });

// BUS ROUTE ==============================================
/**
 * @type {(param: {
 *  arriveTo: {
 *    lat: number,
 *    lng: number
 *  };
 *  departFrom: {
 *    lat: number,
 *    lng: number
 *  };
 * }) => Promise<any>}
 */
export const BusRoute = async ({ arriveTo, departFrom }) => {
  const src = `${departFrom.lng} ${departFrom.lat}`;
  const dst = `${arriveTo.lng} ${arriveTo.lat}`;

  const result = await $.ajax({
    url: `${AS.domains.route}/${
      !window.hlocale || window.hlocale === 'ja' ? 'BusRoute' : 'BusRouteRoma'
    }`,
    type: 'POST',
    contentType: 'application/x-www-form-urlencoded; charset=utf-8',
    data: {
      src,
      dst
    }
  });

  return result;
};
